import { Module, ActionContext } from 'vuex'
import { SdkState, RootState } from '../types'
import {
  getHomeData,
  getCurrentConditionsData,
  getHourlyForecastData,
  getDailyForecastData,
} from '@/services/home.service'
import { RouteLocationNormalized } from 'vue-router'
import { DateTime } from '@/utils/extensions/date-extension'
import AWLocation from '@/models/Location'
import CurrentConditions from '@/models/CurrentConditions'
import Daily from '@/models/Daily'
import HourlyForescast from '@/models/HourlyForecast'
import Minutecast from '@/models/Minutecast'

const home: Module<SdkState, RootState> = {
  state: {
    minutecast: undefined,
    currentConditions: undefined,
    weatherEvents: undefined,
    dailyForecast: undefined,
    hourlyForescast: undefined,
    alerts: undefined,
    activeStorm: null,
    isWinter: false,
    location: undefined,
    locationIndex: 0,
  },
  mutations: {
    setSdkData(state, data: Partial<SdkState>) {
      Object.assign(state, data)
    },
    setCurrentConditions(
      state,
      payload: {
        minutecast: Minutecast | undefined
        currentConditions: CurrentConditions | undefined
      },
    ) {
      state.minutecast = payload.minutecast
      state.currentConditions = payload.currentConditions
    },
    setHourlyForecast(state, hourlyForescast: HourlyForescast[] | undefined) {
      state.hourlyForescast = hourlyForescast
    },
    setDailyForecast(state, dailyForecast: Daily | undefined) {
      state.dailyForecast = dailyForecast
    },
  },
  actions: {
    async fetchSdkData(
      { commit }: ActionContext<SdkState, RootState>,
      p: RouteLocationNormalized | undefined,
    ) {
      try {
        const date = DateTime()
        const hasLongPeriodUpdate = date.getMinutes() > 56 || date.getMinutes() < 4
        const storedLocation = localStorage.getItem('location') ?? ''
        const hasMultipleLocations = ((p?.query.key as string | undefined) ?? '').includes('-')
        const [
          minutecast,
          currentConditions,
          weatherEvents,
          dailyForecast,
          hourlyForescast,
          alerts,
          activeStorm,
          isWinter,
          location,
        ] = await getHomeData(
          p?.params,
          p?.query,
          storedLocation && (JSON.parse(storedLocation) as AWLocation).Key === p?.query.key
            ? (JSON.parse(storedLocation) as AWLocation)
            : undefined,
          {
            hourly: hasLongPeriodUpdate || !this.state.home.hourlyForescast || hasMultipleLocations,
            daily: hasLongPeriodUpdate || !this.state.home.dailyForecast || hasMultipleLocations,
          },
          this.state.home.locationIndex,
        )
        const indices = ((p?.query.key as string | undefined) ?? '')?.split('-')
        const locationIndex = this.state.isFullscreenMenuOpened
          ? this.state.home.locationIndex
          : indices.length > 1 && this.state.home.locationIndex < indices.length - 1
          ? this.state.home.locationIndex + 1
          : 0
        const data = {
          minutecast,
          currentConditions,
          weatherEvents,
          dailyForecast,
          hourlyForescast,
          alerts,
          activeStorm,
          isWinter,
          location,
          locationIndex,
        }

        commit('setSdkData', data)
        commit('setDataError', false)
        return data
      } catch (error) {
        console.error('Error getting sdk data:', error)
        commit('setDataError', true)
        return Promise.reject(error)
      }
    },
    clearSdkData({ commit }: ActionContext<SdkState, RootState>) {
      const data = {
        minutecast: undefined,
        currentConditions: undefined,
        weatherEvents: undefined,
        dailyForecast: undefined,
        hourlyForescast: undefined,
        alerts: undefined,
        activeStorm: null,
        isWinter: false,
        location: undefined,
        locationIndex: 0,
      }
      commit('setSdkData', data)
      return data
    },

    async refreshCurrentConditions({ commit, state }: ActionContext<SdkState, RootState>) {
      const key = state.location?.Key
      if (!key) return
      try {
        const result = await getCurrentConditionsData(key)
        commit('setCurrentConditions', result)
      } catch (e) {
        console.error('[home] refreshCurrentConditions failed:', e)
      }
    },

    async refreshHourlyForecast({ commit, state }: ActionContext<SdkState, RootState>) {
      const key = state.location?.Key
      if (!key) return
      try {
        const hourlyForescast = await getHourlyForecastData(key)
        if (hourlyForescast) commit('setHourlyForecast', hourlyForescast)
      } catch (e) {
        console.error('[home] refreshHourlyForecast failed:', e)
      }
    },

    async refreshDailyForecast({ commit, state }: ActionContext<SdkState, RootState>) {
      const key = state.location?.Key
      if (!key) return
      try {
        const dailyForecast = await getDailyForecastData(key)
        if (dailyForecast) commit('setDailyForecast', dailyForecast)
      } catch (e) {
        console.error('[home] refreshDailyForecast failed:', e)
      }
    },
  },
}

export default home
